import { arrayUnion } from "firebase/firestore";
import { formatItem, formatTimeAmPm, formatTypes } from "global/common/dateFormatting";
import { gEnums } from "global/enums/globalEnums";
import { createRefPath, createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import { dataFix } from 'global/common/dataFix';
import { ParentContext } from "../ParentContext";
import { useAppUserAccess } from "./useAppUserAccess";
import { getBaseRef } from "global/firestoreData/helpers/getBaseRef";
import { useCallback, useContext } from "react";
// import { getSendNotificationProps } from "global/functions/fbScheduledNotifications";
import { _notificationsCollection } from "global/firestoreData/notifications/notificationsData";
import { useFirestore } from "./useFirestore";
import { currentHelpers } from "global/redirection/current";
import { getAppProfileData } from "global/firestoreData/helpers/appProfileData";
import { pageFunctions } from "global/cnr/reducers/PageReducer";
import usePageFunctions from "./usePageFunctions";

const _allowNotificationsUpdates = true
const _lsName = 'notificationsRead'

const dbCallTypes = {
  notifications: 'notifications',
};

export const useNotifications = () => {

  const page_fns = usePageFunctions()

  const parentContext = useContext(ParentContext);
  const { states, fns, fn_cls } = parentContext ?? {}
  const { currentSettings_state, paps_state, eventInfo_state, appNotifications_state } = states ?? {}
  const { pathViews } = paps_state ?? {}
  const { database_fns } = fns ?? {}
  const { getGviDeps } = page_fns ?? {}
  const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { projectModules, appNotifications, logging } = aps_global ?? {}
  const { allowNotifications: allowNotifications_g } = projectModules ?? {}
  const { allowNotifications, allowNotificationTopics } = appNotifications ?? {}
  const _allowNotifications = allowNotifications_g && allowNotifications
  const { notifications_app } = appNotifications_state ?? {}

  const { appProfileData } = eventInfo_state ?? {}

  const appUserAccess = useAppUserAccess()

  const updateNotificationsDirect = useCallback((notification, response_data, callback) => {

    const { clients: clientKey, events: eventKey } = pathViews ?? {}
    const { id: notificationId } = notification ?? {}

    const { result, success } = response_data ?? {}
    const { response_multicast, failedTokens } = result ?? {}
    const { failureCount, successCount } = response_multicast ?? {}

    let _data;

    if (success) {
      _data = { sent: success, readBy: [], sentDate: new Date(), sendData: { failedTokens: failedTokens && failedTokens.length > 0 ? failedTokens : 'none', failureCount: failureCount ? failureCount : 0, successCount: successCount ? successCount : 0 } }
    } else {
      _data = { sent: false }
    }

    if (notification && clientKey && eventKey && notificationId) {
      const _setRef = createRefPath_event(pathViews, [_notificationsCollection, notificationId])
      database_fns.set_doc(_setRef, _data, true, callback)
    }
  }, []);

  /**
   * Updates/creates a notification in the database
   * @param {object} pathViews 
   * @param {object} page_fns 
   * @param {object} notification 
   * @param {object} activeNote 
   * @param {function} callback 
   */
  const updateNotifications = useCallback((notification, activeNote, callback) => {

    if (notification && getGviDeps) {

      dataFix.removeAllEmpties(notification)

      const viDeps = getGviDeps(_notificationsCollection)
      const baseRef = getBaseRef(viDeps, pathViews)
      const { refPath } = baseRef
      const _editRef = activeNote ? createRefPath([_notificationsCollection, activeNote.id], refPath) : createRefPath([_notificationsCollection], refPath)
      delete notification.notificationRecipients
      delete notification.currentGroupKey
      delete notification.appUserCollection
      delete notification.appUserViewTypeProp
      const _notification = dataFix.removeUnderscoreKeys(notification)

      // add or update 
      if (_notification.notificationScheduleType === gEnums.notificationScheduleTypes.direct) {
        const currentDate = new Date()
        _notification.active = true
        _notification.sent = true
        _notification.sendDate = formatItem(formatTypes.shortestDate, currentDate)
        _notification.sendTime = formatTimeAmPm(currentDate.getHours() + ':' + currentDate.getMinutes())
      }
      if (_allowNotificationsUpdates) {
        if (activeNote) {
          database_fns.set_doc(_editRef, _notification, true, callback)
        } else {
          database_fns.add_doc(_editRef, _notification, callback)
        }
      } else {
        callback(notification)
      }
    }
  }, []);

  const updateMarkAsRead = useCallback((pathViews, noteId, callback) => {

    if (noteId) {

      const { uid } = appUserAccess ?? {}

      if (uid) {
        const viDeps = getGviDeps(_notificationsCollection)
        const baseRef = getBaseRef(viDeps, pathViews)
        const { refPath } = baseRef
        const _editRef = createRefPath([_notificationsCollection, noteId], refPath)
        const data = { readBy: arrayUnion(uid) }
        // database_fns.update_doc(_editRef, data, callback)
        database_fns.update_docc({
          refPath: _editRef,
          data: data,
          callback
        })
      }
    }
  }, []);

  const removeNotification = useCallback(({ noteId, callback }) => {

    if (noteId) {
      const viDeps = getGviDeps(_notificationsCollection)
      const baseRef = getBaseRef(viDeps, pathViews)
      const { refPath } = baseRef

      // add or update
      const _deleteRef = createRefPath([_notificationsCollection, noteId], refPath)
      database_fns.delete_doc(_deleteRef, callback)
    }
  }, []);

  const sendNotification = useCallback((activeNote, notification, callback) => {
    console.log('activeNote', activeNote)
    const handleSuccess = () => {

    }

    if (activeNote) {
      const np = getSendNotificationProps(activeNote, appUserAccess, handleSuccess, notification, page_fns, pathViews, appProfileData, allowNotificationTopics)
      console.log('np', np)
    }
  }, []);

  const sendNotificationToSelf = useCallback((notificationData, callback) => {
    fn_cls.sendNotificationToSelf({ notificationData, appUserAccess, pathViews, callback })
  }, []);

  const updateScheduledNotifications = useCallback((dataUpdate, pathViews, callback) => {
    if (dataUpdate && pathViews) {
      const _pathKey = 'clients_' + pathViews.clients + '_events_' + pathViews.events
      const _refPath = createRefPath(['notificationSchedules', _pathKey])
      console.log('_refPath', _refPath, dataUpdate)
      database_fns.set_doc(_refPath, dataUpdate, false, callback)
    }
  }, []);

  const getScheduledNotifications = useCallback((callback) => {
    if (pathViews) {
      const _pathKey = 'clients_' + pathViews.clients + '_events_' + pathViews.events
      const _refPath = createRefPath(['notificationSchedules', _pathKey])
      // database_fns.get_doc({ refPath: _refPath, callback })
      database_fns.get_data({ refPath: _refPath, callback, opts: { returnFirstObject: true, ignoreId: true } })
    }
  }, []);

  const getAppNotifications = useCallback(() => {
    const { data: _notifications } = useFirestore({ items: _allowNotifications && pathViews?.events ? [_notificationsCollection] : null })
    return { _notifications }
  }, []);

  const markAsSeen = useCallback(({ note, notifications_state, notifications }) => {
    const { _itemKey } = note ?? {}
    updateLocalStorage(_itemKey)
    const { appUserAccess } = notifications_state ?? {}
    const notifications_sent = notifications ? _.filter(notifications, { sent: true, active: true }) : null
    const _notificationz_appUser = getNotifications_appUser(appUserAccess, aps_appUserSettings, notifications_sent);
    return { notifications_appUser: _notificationz_appUser }
  }, []);

  const notificationClose = useCallback(({ notifications_state, notifications, inApp, noteKey, callback }) => {
    if (inApp && noteKey) {
      const { appNotifications, appUserAccess } = notifications_state ?? {}
      const notifications_sent = notifications ? _.filter(notifications, { sent: true, active: true }) : null
      const { markAsReadInDatabase } = appNotifications ?? {}
      if (markAsReadInDatabase) {
        updateMarkAsRead(pathViews, noteKey, markAsReadInDatabase)
      } else {
        updateLocalStorage(noteKey)
        const _notifications_appUser = getNotifications_appUser(appUserAccess, aps_appUserSettings, notifications_sent);
        callback(_notifications_appUser)
      }
    }
  }, []);

  const checkScheduledNotifications = useCallback((props) => {
    checkTheScheduledNotifications({
      database_fns,
      fn_cls,
      appUserAccess,
      aps_appUserSettings,
      logging,
      updateNotifications,
      props
    })
  }, []);

  return {
    getAppNotifications,
    getScheduledNotifications,
    markAsSeen,
    removeNotification,
    sendNotification,
    updateMarkAsRead,
    updateNotifications,
    updateNotificationsDirect,
    updateScheduledNotifications,
    notificationClose,
    checkScheduledNotifications,
    sendNotificationToSelf,
  };

};

const updateLocalStorage = (noteKey) => {
  const lsExistingValue = currentHelpers.storageItem_get(_lsName)
  const lsValue = lsExistingValue ? JSON.parse(lsExistingValue) : []
  if (!lsValue.includes(noteKey)) { lsValue.push(noteKey) }
  currentHelpers.storageItem_set(_lsName, JSON.stringify(lsValue))
}

const getNotifications_appUser = (appUserAccess, aps_appUserSettings, notifications_sent) => {

  const { appUserSession, appUserAccessType } = appUserAccess ?? {}
  const { appUserViewTypeProp } = aps_appUserSettings ? aps_appUserSettings : { appUserViewTypeProp: 'registrationTypes' }

  const aun = []
  let auKey = { groupKey: null, itemKey: null };

  const _readNotes = getLocalStorage()
  const _isAdmin = appUserAccess && (appUserAccess.accessLevel >= gEnums.accessLevels.admin.value)

  if (appUserAccess && notifications_sent) {

    if (appUserSession || _isAdmin) {

      const _appUser_viewType = appUserSession ? appUserSession[appUserViewTypeProp] : null

      auKey.groupKey = appUserViewTypeProp
      auKey.itemKey = _appUser_viewType


      // loop the sent notifications
      notifications_sent.forEach(notification => {

        let allow = true
        if (notification.sendDate && notification.sendTime) {
          notification.sendDateUTC = getStartTimeUtc(notification.sendDate, notification.sendTime)
          notification.sendTimeUTC = notification.sendDateUTC.getTime()
        }

        // check to see if the note has been read by the appUser
        notification.read = _readNotes && _readNotes.includes(notification._itemKey) ? true : false

        if (allow) {

          const { readBy, targetOptions } = notification ?? {}

          const { notificationRecipientKeys, pageItem, pageItemKey, subKey, subItem, subKeys, recipientItem } = targetOptions ?? {}

          let _allowNotification = false
          const { _itemKey, appUserSessionKey } = appUserSession ?? {}

          if (notificationRecipientKeys && _.isArray(notificationRecipientKeys) && notificationRecipientKeys.length > 0) {
            _allowNotification = notificationRecipientKeys.includes(_itemKey) || notificationRecipientKeys.includes(appUserSessionKey) || _isAdmin
          } else if (pageItem && pageItemKey) {
            // see if the appUser has the `pageItemKey` in its `pageItem` prop
            const appUserItemValue = appUserSession && appUserSession[pageItem] ? appUserSession[pageItem] : null
            if (appUserItemValue && _.isArray(appUserItemValue) && appUserItemValue.includes(pageItemKey)) {
              _allowNotification = true
            }
          } else if (subItem) {
            // see if the appUser's subItem is = to the subKey
            if (subKey && appUserSession && appUserSession[subItem] && appUserSession[subItem] === subKey) {
              _allowNotification = true
            }
            if (subKeys && appUserSession && appUserSession[subItem] && subKeys.includes(appUserSession[subItem])) {
              _allowNotification = true
            }
          } else {
            _allowNotification = true
          }

          if (recipientItem && appUserSessionKey) {
            _allowNotification = true
          }

          if (_allowNotification) {
            addNoteItem(aun, notification, readBy, appUserAccess.uid)
          }
        }
      })
    } else {
      notifications_sent.forEach(sn => {
        addAdminNoteItems(appUserAccessType, aun, appUserAccess, sn, _readNotes)
      })
    }
  }

  const aunts = {
    all: aun,
    read: _.filter(aun, { read: true }),
    unread: _.filter(aun, { read: false }),
  }

  aunts.hasNew = aunts.unread && aunts.unread.length > 0 ? true : false

  return aunts

}

const checkTheScheduledNotifications = ({
  database_fns,
  fn_cls,
  appUserAccess,
  aps_appUserSettings,
  logging,
  updateNotifications,
  props
}) => {
  const {
    action,
    appNotifications,
    aps_viewItems,
    notifications,
    page_fns,
    pathViews,
    allowNotificationTopics,
  } = props

  const { appUserCollection, appUserViewTypeProp } = aps_appUserSettings ?? {}
  const { pushActiveToSelf, notificationDeliveryType } = appNotifications ?? {}

  const seconds_now = Math.floor(Date.now() / 1000);

  if (notifications) {
    // this will send notifications if 
    // active
    // sendDate is after now

    // loop the notifications
    Object.keys(notifications).forEach(key => {

      const note = notifications[key]
      const { autoSend, sent, active, sendDateAndTimeUtc, notificationDeliveryType: notificationDeliveryType_note, notificationScheduleType } = note ? note : {}

      const _notificationDeliveryType = notificationDeliveryType_note ? notificationDeliveryType_note : notificationDeliveryType

      if (autoSend) {

        switch (notificationScheduleType) {

          case gEnums.notificationScheduleTypes.scheduled:

            if (!sent && active && sendDateAndTimeUtc) {

              const noteDate = sendDateAndTimeUtc
              const allowSend = seconds_now >= noteDate.seconds

              if (allowSend) {

                switch (_notificationDeliveryType) {

                  case gEnums.notificationDeliveryTypes.inApp:
                    const _note = { ...note }
                    _note.sent = true
                    _note.active = true
                    updateNotifications(database_fns, pathViews, page_fns, _note, _note)
                    break;

                  case gEnums.notificationDeliveryTypes.email:
                    const nd = {
                      email: {
                        recipient: _developerAdminEmail,
                        sender: 'support@meetingevolution.net',
                        subject: note.title,
                        body: note.body,
                      }
                    }
                    fn_cls.sendEmail(nd)
                    break;

                  case gEnums.notificationDeliveryTypes.push:
                    if (pushActiveToSelf) {
                      fn_cls.sendNotificationByData(notificationMethodTypes.firebaseApi, note, pathViews, appUserAccess.fcmTokens).then(res => {
                        const { success, result } = res ?? {}
                        if (success) { console.log('result', result) }
                        action.callback && action.callback()
                      })
                    } else {
                      sendScheduledPushNotifications({
                        appUserCollection,
                        appUserViewTypeProp,
                        aps_viewItems,
                        pathViews,
                        notificationData: note,
                        allowNotificationTopics
                      })
                    }
                    break;

                  case gEnums.notificationDeliveryTypes.sms:
                    const data = {
                      to: '+14126002081',
                      text: note.body,
                    }
                    fn_cls.sendSms(data, logging)
                    break;


                  default:
                    break;
                }
              } else {
                console.error("note not send", note)
              }
            }
            break;
          default:
            break;
        }
      }
    })
  }
}

const sendScheduledPushNotifications = (props) => {
  const {
    pathViews,
    page_fns,
    appUserCollection,
    appUserCollections,
    appUserViewTypeProp,
    aps_viewItems,
    notificationData,
    allowNotificationTopics
  } = props
  const np = getSendNotificationProps(null, null, null, notificationData, page_fns, pathViews, null, allowNotificationTopics)
  // appUserCollections: state.appUserCollections, aps_viewItems: state.aps_viewItems, pathViews: state.pathViews
  getAppProfileData({ pathViews, page_fns, appUserCollection, appUserCollections, appUserViewTypeProp, aps_viewItems, callback_send: sendTheScheduledNotification, notificationData, allowNotificationTopics, np })
}


export const sendTheScheduledNotification = (props) => {
  const {
    // appUserAccess,
    callback,
    notificationData,
    pathViews,
    appProfileData,
    // allowNotificationTopics,
  } = props

  const { selectedTopicGroups } = notificationData
  // if (allowNotificationTopics && selectedTopicGroups &&  appProfileData && appProfileData[topicOriginGroup] && appProfileData[topicOriginGroup][topicOriginGroupKey]) {
  //   if (_allowNotificationSend) {
  //     sendNotification_topic(notificationData, appUserAccess, paps_state).then(res => {
  //       callback && callback()
  //     })
  //   } else {
  //     console.log('sendTheNotification - notificationTopic', notificationData)
  //   }
  // } else {
  if (appProfileData && selectedTopicGroups && Object.keys(selectedTopicGroups).length > 0) {
    let fcms = [];
    Object.keys(selectedTopicGroups).forEach(key => {
      const selectedTopicGroup = selectedTopicGroups[key]
      const { groupKey, itemKey } = selectedTopicGroup
      const topicGroup = groupKey && itemKey ? appProfileData[groupKey][itemKey] : appProfileData[groupKey]
      if (topicGroup) {
        appendTopicGroupFcms(fcms, topicGroup)
      }
    })
    if (fcms) {
      if (_allowNotificationSend) {
        fn_cls.sendNotificationByData(notificationMethodTypes.firebaseApi, notificationData, pathViews, fcms, null, null, callback)
      } else {
        console.log('fcms', fcms)
      }
    }
  }
}


/** Returns the fcm tokes for each item in the topicGroup */
const appendTopicGroupFcms = (fcms, topicGroup) => {
  const { targets } = topicGroup
  if (targets) {
    Object.keys(targets).forEach(key => {
      const target = targets[key]
      const { fcmTokens } = target
      if (fcmTokens) {
        fcmTokens.forEach(fcmToken => {
          if (!fcms.includes(fcmToken)) {
            fcms.push(fcmToken)
          }
        })
      }
    })
  } else {
    Object.keys(topicGroup).forEach(key => {
      const target = topicGroup[key]
      const { fcmTokens } = target
      if (fcmTokens) {
        fcmTokens.forEach(fcmToken => {
          if (!fcms.includes(fcmToken)) {
            fcms.push(fcmToken)
          }
        })
      }
    })
  }
}

const getSendNotificationProps = (activeNote, appUserAccess, callback, notificationData, page_fns, pathViews, appProfileData, allowNotificationTopics) => {
  return { activeNote, appUserAccess, callback, notificationData, page_fns, pathViews, appProfileData, allowNotificationTopics }
}

const getLocalStorage = () => currentHelpers.storageItem_get(_lsName)